// Reusable, generic table. Columns declare a header + a cell renderer. Keeps
// list views (clients, tasks, tokens, audit, ...) consistent and uniform.
//
// H40 — sortable: a column that supplies `sortValue` becomes click-to-sort. Clicking its
// header cycles none → asc → desc → none, with a direction indicator (▲/▼) and aria-sort.
// Sorting is STABLE (original order is the tiebreak) and pure (no mutation of `rows`).
// Toggle/enabled/active columns should be placed FIRST in the `columns` array (H40 spec).

import { useMemo, useState, type ReactNode } from 'react';

export type SortValue = string | number | boolean | null | undefined;
export type SortDir = 'asc' | 'desc';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Cell renderer; receives the row. */
  cell: (row: T) => ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  /** Provide to make this column sortable (header becomes clickable). */
  sortValue?: (row: T) => SortValue;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Optional initial sort (column key + direction). */
  initialSort?: { key: string; dir: SortDir };
}

function compare(a: SortValue, b: SortValue): number {
  // Nullish sorts last in ascending order.
  const an = a == null || a === '';
  const bn = b == null || b === '';
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? -1 : 1;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function DataTable<T>({ columns, rows, rowKey, empty, onRowClick, initialSort }: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(initialSort ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const getter = col.sortValue;
    const dir = sort.dir === 'asc' ? 1 : -1;
    // decorate-sort-undecorate keeps the sort STABLE via the original index tiebreak.
    return rows
      .map((row, i) => ({ row, i }))
      .sort((x, y) => {
        const c = compare(getter(x.row), getter(y.row));
        return c !== 0 ? c * dir : x.i - y.i;
      })
      .map((d) => d.row);
  }, [rows, columns, sort]);

  function onHeaderClick(c: Column<T>) {
    if (!c.sortValue) return;
    setSort((prev) => {
      if (!prev || prev.key !== c.key) return { key: c.key, dir: 'asc' };
      if (prev.dir === 'asc') return { key: c.key, dir: 'desc' };
      return null; // third click clears the sort
    });
  }

  if (rows.length === 0) {
    return <div className="empty">{empty ?? 'No data.'}</div>;
  }
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = !!c.sortValue;
              const active = sort?.key === c.key;
              const ariaSort = active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <th
                  key={c.key}
                  style={{ width: c.width, textAlign: c.align ?? 'left' }}
                  aria-sort={sortable ? ariaSort : undefined}
                  className={sortable ? `sortable${active ? ' sorted' : ''}` : undefined}
                  onClick={sortable ? () => onHeaderClick(c) : undefined}
                  role={sortable ? 'button' : undefined}
                  tabIndex={sortable ? 0 : undefined}
                  onKeyDown={
                    sortable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onHeaderClick(c);
                          }
                        }
                      : undefined
                  }
                >
                  <span className="th-label">{c.header}</span>
                  {sortable && (
                    <span className="sort-ind" aria-hidden="true">
                      {active ? (sort!.dir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={rowKey(row)}
              className={onRowClick ? 'clickable' : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
